import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { getSiteOrigin } from 'src/seo/getSiteOrigin'
import { site } from 'src/seo/site'

type SeoProps = {
  title?: string
  description?: string
  imageUrl?: string
  canonicalPath?: string
  noindex?: boolean
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>
}

function getAbsoluteUrl(origin: string, pathOrUrl: string) {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  if (!pathOrUrl.startsWith('/')) return `${origin}/${pathOrUrl}`
  return `${origin}${pathOrUrl}`
}

function organizationJsonLd(origin: string) {
  return {
    '@type': 'Organization',
    '@id': `${origin}/#organization`,
    name: site.legalName,
    url: origin,
    email: site.contacts.email,
    telephone: site.contacts.phone,
    address: {
      '@type': 'PostalAddress',
      addressCountry: site.address.country,
      addressLocality: site.address.locality,
      streetAddress: site.address.streetAddress
    }
  }
}

function localBusinessJsonLd(origin: string) {
  return {
    '@type': 'LocalBusiness',
    '@id': `${origin}/#localbusiness`,
    name: site.legalName,
    url: origin,
    email: site.contacts.email,
    telephone: site.contacts.phone,
    address: {
      '@type': 'PostalAddress',
      addressCountry: site.address.country,
      addressLocality: site.address.locality,
      streetAddress: site.address.streetAddress
    }
  }
}

export const Seo: React.FC<SeoProps> = ({
  title,
  description,
  imageUrl,
  canonicalPath,
  noindex,
  jsonLd
}) => {
  const { pathname, search } = useLocation()

  const origin = getSiteOrigin()

  const resolvedTitle = title ? `${title} | ${site.defaultTitle}` : site.defaultTitle
  const resolvedDescription = description || site.defaultDescription

  const canonical = origin
    ? getAbsoluteUrl(origin, canonicalPath ?? `${pathname}${search}`)
    : canonicalPath ?? `${pathname}${search}`

  const ogImage = origin
    ? getAbsoluteUrl(origin, imageUrl ?? site.defaultOgImagePath)
    : imageUrl ?? site.defaultOgImagePath

  const extraBlocks = jsonLd == null ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd]
  const filteredExtras = extraBlocks.filter(Boolean) as Record<string, unknown>[]

  const resolvedJsonLd = origin
    ? {
        '@context': 'https://schema.org',
        '@graph': [organizationJsonLd(origin), localBusinessJsonLd(origin), ...filteredExtras]
      }
    : filteredExtras.length > 0
      ? { '@context': 'https://schema.org', '@graph': filteredExtras }
      : undefined

  return (
    <Helmet>
      <html lang='uk' />

      <title>{resolvedTitle}</title>
      <meta name='description' content={resolvedDescription} />

      <link rel='canonical' href={canonical} />

      <meta property='og:site_name' content={site.name} />
      <meta property='og:type' content='website' />
      <meta property='og:locale' content={site.defaultLocale} />
      <meta property='og:title' content={resolvedTitle} />
      <meta property='og:description' content={resolvedDescription} />
      <meta property='og:url' content={canonical} />
      <meta property='og:image' content={ogImage} />

      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={resolvedTitle} />
      <meta name='twitter:description' content={resolvedDescription} />
      <meta name='twitter:image' content={ogImage} />

      {noindex ? <meta name='robots' content='noindex, nofollow' /> : <meta name='robots' content='index, follow' />}

      {resolvedJsonLd ? (
        <script type='application/ld+json'>{JSON.stringify(resolvedJsonLd)}</script>
      ) : null}
    </Helmet>
  )
}

